import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useCheckout — single entry point for "contratar plan X" anywhere in the app.
 *
 * Works identically in two modes (driven by backend env PAYMENTS_MODE):
 *   - simulation (current): subscription is activated instantly without
 *     Stripe. The hook redirects to /app/billing/success and the user's
 *     plan is active when they get there.
 *   - stripe (future Sprint 12): hook receives a Stripe checkout URL and
 *     does window.location.assign() to it.
 *
 * Returns:
 *   {
 *     mode: 'simulation'|'stripe'|null,
 *     isSimulation: boolean,
 *     checking: boolean,    // mode being fetched
 *     submitting: boolean,  // POST /checkout in flight
 *     checkout: (planId, opts?) => Promise<void>
 *   }
 *
 * Usage:
 *   const { checkout, submitting } = useCheckout();
 *   <Button onClick={() => checkout('performance_individual')}>Contratar</Button>
 */
export const useCheckout = () => {
  const navigate = useNavigate();
  const { refreshStatus } = useAuth();
  const [mode, setMode] = useState(null);
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/billing/mode`);
        if (!ignore) setMode(res.data?.mode || 'simulation');
      } catch {
        if (!ignore) setMode('simulation'); // safe default
      } finally {
        if (!ignore) setChecking(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const checkout = useCallback(async (planId, opts = {}) => {
    if (!planId) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        plan_id: planId,
        success_url: opts.successUrl || null,
        cancel_url: opts.cancelUrl || null,
      });

      if (res.data?.status === 'free_plan') {
        toast.success('El plan gratuito no requiere pago. Continuando…');
        navigate(res.data.redirect_url || '/app/dashboard');
        return;
      }

      if (res.data?.mode === 'stripe' && res.data?.redirect_url) {
        // Future Sprint 12: hand off to Stripe-hosted checkout (external URL).
        // Payment can't be bypassed, so we always redirect regardless of opts.
        window.location.assign(res.data.redirect_url);
        return res.data;
      }

      // Simulation mode (current default): subscription is already active
      if (res.data?.status === 'activated') {
        toast.success('Plan activado en modo simulación. ¡Bienvenido!');
      } else if (res.data?.status === 'already_active') {
        toast.info('Ya tienes este plan activo.');
      }

      // CRITICAL: refresh auth/user status so subsequent ProtectedRoute
      // checks see the new subscription_status and don't bounce the user
      // to an onboarding step they already passed. Failure to refresh is
      // not fatal — fall through to the redirect anyway.
      try {
        await refreshStatus();
      } catch (_) {
        /* ignore — redirect handles eventual consistency */
      }

      // Caller wants to control post-activation flow itself (e.g. onboarding
      // needs to save the mode and go to the assessment). We already
      // activated + refreshed status; hand the data back without navigating.
      if (opts.redirect === false) {
        return res.data;
      }

      // Build internal redirect — backend sometimes returns the literal
      // placeholder "{SESSION_ID}". Resolve it here so navigate() gets a
      // clean SPA path.
      let redirectUrl = res.data?.redirect_url || '/app/dashboard';
      if (res.data?.session_id) {
        redirectUrl = redirectUrl.replace('{SESSION_ID}', res.data.session_id);
      }
      // If it's an external URL (starts with http), do a hard navigate;
      // otherwise use react-router so the SPA stays alive (no black screen).
      if (/^https?:\/\//i.test(redirectUrl)) {
        window.location.assign(redirectUrl);
      } else {
        navigate(redirectUrl, { replace: true });
      }
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.message ||
        'No se pudo iniciar el checkout';
      toast.error(typeof msg === 'string' ? msg : 'Error al contratar el plan');
      console.error('Checkout error', err);
    } finally {
      setSubmitting(false);
    }
  }, []);

  return {
    mode,
    isSimulation: mode !== 'stripe',
    checking,
    submitting,
    checkout,
  };
};

export default useCheckout;
